import api from './api';

export interface ActivityRecord {
  id: string;
  user_id: string;
  user_email: string | null;
  tenant_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  resource_name: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  status: 'success' | 'failure';
  http_method: string | null;
  http_path: string | null;
  http_status: number | null;
  created_at: string;
}

export interface ActivityListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  resourceType?: string;
  resourceId?: string;
  userId?: string;
  /**
   * REQUIRED for a SuperAdmin. `/activity` is excluded from the api.ts tenant
   * injection, and the service refuses to run an unscoped query (an unfiltered
   * scan of activity_log once OOM-killed MySQL) — it returns an empty page
   * instead. So a SuperAdmin caller that does not pass a tenant gets silence,
   * not an error.
   */
  tenantId?: string;
  /** ISO instants; filtered in SQL rather than in the caller. */
  dateFrom?: string;
  dateTo?: string;
  /**
   * Keep only actions starting with one of these, applied in SQL (doc 22
   * §10.3).
   *
   * Filtering client-side is what this replaces: the endpoint pages BEFORE it
   * filters, so on a busy tenant the rows a caller wanted were already gone
   * from the page by the time the filter ran.
   */
  actionPrefix?: string[];
}

export interface PaginatedActivityResponse {
  items: ActivityRecord[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const activityService = {
  /**
   * Query activities (generic)
   */
  list: async (params?: ActivityListParams) => {
    // actionPrefix goes on the wire COMMA-JOINED, not as a repeated parameter.
    // Axios' default serializer writes an array as `actionPrefix[]=a`, and the
    // bracketed name is not what the API reads — the filter would be silently
    // dropped and the panel would show every action again, which is exactly the
    // bug this parameter exists to fix.
    const { actionPrefix, ...rest } = params ?? {};
    const response = await api.get('/activity', {
      params: {
        ...rest,
        ...(actionPrefix?.length ? { actionPrefix: actionPrefix.join(',') } : {}),
      },
    });
    return response.data as PaginatedActivityResponse;
  },

  /**
   * Get activities for a specific user
   */
  getByUser: async (userId: string, params?: { page?: number; pageSize?: number; search?: string }) => {
    const response = await api.get(`/users/${userId}/activity`, { params });
    return response.data as PaginatedActivityResponse;
  },

  /**
   * Export user activity as CSV
   */
  exportCSV: async (userId: string): Promise<Blob> => {
    const response = await api.get(`/users/${userId}/activity/export`, {
      responseType: 'blob',
    });
    // The axios response interceptor already unwraps to the body, which for a
    // blob response IS the Blob.
    return response as unknown as Blob;
  },
};

export default activityService;
